import { useFormik } from "formik";
import React, { useContext, useEffect } from "react";
import { toast } from "sonner";
import { UserService } from "../../../services/user/userService";
import { invalidateBlockedEntryProductStatusesCache } from "../../../hooks/useBlockedEntryProductStatuses";
import { isEqual } from "../../../utils/objectFunctions";
import yup from "../../../yupExtensions";

type SettingsProviderProps = {
    children: React.ReactNode,
}

type SettingsContextData = {
    companyConfig: any,
    setCompanyConfig: (companyConfig: any) => void,
    updateCompanyConfig: (key: string, value: any) => void,
    isLoading: boolean,
    setIsLoading: (isLoading: boolean) => void,
    hasCompanyConfigChanges: boolean,
    applyCompanyConfigChanges: () => void,
    discardCompanyConfigChanges: () => void,
    showModal: boolean,
    setShowModal: (showModal: boolean) => void,
    showCacheModal: boolean,
    setShowCacheModal: (showCacheModal: boolean) => void,
    showImportCRMModal: boolean,
    setShowImportCRMModal: (showImportCRMModal: boolean) => void,
    originalCompanyConfig: any,
    formik: any,
}

const SettingsContext: React.Context<SettingsContextData> = React.createContext<SettingsContextData>({
    companyConfig: null,
    setCompanyConfig: (newCompanyConfig: any) => { },
    updateCompanyConfig: (key: string, value: any) => { },
    isLoading: true,
    setIsLoading: (isLoading: boolean) => { },
    hasCompanyConfigChanges: false,
    applyCompanyConfigChanges: () => { },
    discardCompanyConfigChanges: () => { },
    showModal: false,
    setShowModal: (showModal: boolean) => { },
    showCacheModal: false,
    setShowCacheModal: (showCacheModal: boolean) => { },
    showImportCRMModal: false,
    setShowImportCRMModal(showImportCRMModal: boolean) { },
    originalCompanyConfig: null,
    formik: null,
});

const settingsSchema = yup.object({
    daysPeriodToEndContract: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    contractCodeFormat: yup.string().required('Campo obligatorio'),
    incidenceCodeFormat: yup.string().notRequired(),
    nonStandardInvoiceCodeFormat: yup.string().nullable().notRequired(),
    festiveDays: yup.array().of(
        yup.object({
            concept: yup.string().required('Campo obligatorio'),
            date: yup.string().required('Campo obligatorio'),
        })
    ),
    legalText: yup.string().required('Campo obligatorio'),
    smsText: yup.string().required('Campo obligatorio'),
    daysNeededToGenerateContract: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    tutorMaxMorningTutorials: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    tutorMaxAfternoonTutorials: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    daysBeforeExpirationCollectionDate: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    daysAfterNonPaymentExpiration: yup.number().integer('Debe ser un número entero').min(1, 'El tiempo mínimo es de 1 día').required('Campo obligatorio'),
    activeContractCommissionModel: yup.string().required('Campo obligatorio'),
    blockedContractProductStatuses: yup.array().of(yup.string()).nullable(),
    facilitatorRoleIds: yup.array().of(yup.string()).nullable(),
    facilitatorManagerRoleIds: yup.array().of(yup.string()).nullable(),
    facilitatorOrderStatusIds: yup.array().of(yup.string()).nullable(),
});

const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {

    const userService = new UserService();
    const [companyConfig, setCompanyConfig] = React.useState<any>(null);
    const [originalCompanyConfig, setOriginalCompanyConfig] = React.useState<any>(null);
    const [hasCompanyConfigChanges, setHasCompanyConfigChanges] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [showCacheModal, setShowCacheModal] = React.useState<boolean>(false);
    const [showImportCRMModal, setShowImportCRMModal] = React.useState<boolean>(false);

    useEffect(() => {
        fetchCompanyConfig();
    }, [])

    const fetchCompanyConfig = () => {
        setIsLoading(true);
        userService.getCompanyConfiguration().then(response => {
            let fetchedData = response.getResponseData()?.data;
            fetchedData.festiveDays = fetchedData && fetchedData?.festiveDays ? fetchedData?.festiveDays.map((day: any) => {
                return {
                    id: day.id,
                    concept: day.concept,
                    date: day.date.date.split(' ')[0],
                }
            }) : [];
            setCompanyConfig(fetchedData);
            setOriginalCompanyConfig(fetchedData);
            setIsLoading(false);
            setHasCompanyConfigChanges(false);
            formik.setValues({
                daysPeriodToEndContract: fetchedData?.daysPeriodToEndContract ?? '',
                contractCodeFormat: fetchedData?.contractCodeFormat ?? '',
                tutorAgreementDocumentTemplate: fetchedData?.tutorAgreementDocumentTemplate ?? '',
                incidenceCodeFormat: fetchedData?.incidenceCodeFormat ?? '',
                nonStandardInvoiceCodeFormat: fetchedData?.nonStandardInvoiceCodeFormat ?? '',
                festiveDays: fetchedData?.festiveDays ?? [],
                legalText: fetchedData?.legalText ?? '',
                smsText: fetchedData?.smsText ?? '',
                daysNeededToGenerateContract: fetchedData?.daysNeededToGenerateContract ?? '',
                tutorMaxMorningTutorials: fetchedData?.tutorMaxMorningTutorials ?? 1,
                tutorMaxAfternoonTutorials: fetchedData?.tutorMaxMorningTutorials ?? 1,
                daysBeforeExpirationCollectionDate: fetchedData?.daysBeforeExpirationCollectionDate ?? 2,
                daysAfterNonPaymentExpiration: fetchedData?.daysAfterNonPaymentExpiration ?? 2,
                activeContractCommissionModel: fetchedData?.activeContractCommissionModel ?? '',
                blockedContractProductStatuses: fetchedData?.blockedContractProductStatuses ?? [],
                facilitatorRoleIds: fetchedData?.facilitatorRoleIds ?? [],
                facilitatorManagerRoleIds: fetchedData?.facilitatorManagerRoleIds ?? [],
                facilitatorOrderStatusIds: fetchedData?.facilitatorOrderStatusIds ?? [],
            });
        }).catch(error => {
            console.log('Error fetching company config: ', error);
            setIsLoading(false);
        });
    }

    const formik = useFormik({
        initialValues: {
            daysPeriodToEndContract: companyConfig?.daysPeriodToEndContract ?? '',
            contractCodeFormat: companyConfig?.contractCodeFormat ?? '',
            tutorAgreementDocumentTemplate: companyConfig?.tutorAgreementDocumentTemplate ?? '',
            incidenceCodeFormat: companyConfig?.incidenceCodeFormat ?? null,
            nonStandardInvoiceCodeFormat: companyConfig?.nonStandardInvoiceCodeFormat ?? '',
            festiveDays: companyConfig?.festiveDays ?? [],
            legalText: companyConfig?.legalText ?? '',
            smsText: companyConfig?.smsText ?? '',
            daysNeededToGenerateContract: companyConfig?.daysNeededToGenerateContract ?? '',
            tutorMaxMorningTutorials: companyConfig?.tutorMaxMorningTutorials ?? 1,
            tutorMaxAfternoonTutorials: companyConfig?.tutorMaxMorningTutorials ?? 1,
            daysBeforeExpirationCollectionDate: companyConfig?.daysBeforeExpirationCollectionDate ?? 2,
            daysAfterNonPaymentExpiration: companyConfig?.daysAfterNonPaymentExpiration ?? 2,
            activeContractCommissionModel: companyConfig?.activeContractCommissionModel ?? '',
            blockedContractProductStatuses: companyConfig?.blockedContractProductStatuses ?? [],
            facilitatorRoleIds: companyConfig?.facilitatorRoleIds ?? [],
            facilitatorManagerRoleIds: companyConfig?.facilitatorManagerRoleIds ?? [],
            facilitatorOrderStatusIds: companyConfig?.facilitatorOrderStatusIds ?? [],
        },
        validationSchema: settingsSchema,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: (values) => {
        },
    });

    //////////////////////////////////////////////////////////////////////
    // FUNTION TO KEEP COMPANY CONFIG NEW VALUES IN PROVIDER
    //////////////////////////////////////////////////////////////////////
    const updateCompanyConfig = (key: string, value: any) => {
        const newCompanyConfig = { ...companyConfig, [key]: value };
        setCompanyConfig(newCompanyConfig);
        if (!isEqual(newCompanyConfig, originalCompanyConfig)) setHasCompanyConfigChanges(true);
        else setHasCompanyConfigChanges(false);
    };

    //////////////////////////////////////////////////////////////////////
    // FUNCTION TO APPLY COMPANY CONFIG CHANGES
    //////////////////////////////////////////////////////////////////////
    const applyCompanyConfigChanges = () => {
        const configurationParameters = {
            configurationParameters: {
                contractCodeFormat: companyConfig.contractCodeFormat,
                daysPeriodToEndContract: companyConfig.daysPeriodToEndContract as number,
                tutorAgreementDocumentTemplate: companyConfig.tutorAgreementDocumentTemplate !== formik.values.tutorAgreementDocumentTemplate
                    ? formik.values.tutorAgreementDocumentTemplate
                    : companyConfig.tutorAgreementDocumentTemplate,
                incidenceCodeFormat: companyConfig.incidenceCodeFormat,
                festiveDays: companyConfig?.festiveDays?.map((day: any) => {
                    return {
                        concept: day.concept,
                        date: day.date,
                    }
                }),
                legalText: companyConfig.legalText,
                smsText: companyConfig.smsText,
                daysNeededToGenerateContract: companyConfig.daysNeededToGenerateContract as number,
                tutorMaxMorningTutorials: companyConfig.tutorMaxMorningTutorials as number,
                tutorMaxAfternoonTutorials: companyConfig.tutorMaxAfternoonTutorials as number,
                daysBeforeExpirationCollectionDate: companyConfig.daysBeforeExpirationCollectionDate as number,
                daysAfterNonPaymentExpiration: companyConfig.daysAfterNonPaymentExpiration as number,
                activeContractCommissionModel: companyConfig.activeContractCommissionModel,
                blockedContractProductStatuses: companyConfig.blockedContractProductStatuses ?? [],
                facilitatorRoleIds: companyConfig.facilitatorRoleIds ?? [],
                facilitatorManagerRoleIds: companyConfig.facilitatorManagerRoleIds ?? [],
                facilitatorOrderStatusIds: companyConfig.facilitatorOrderStatusIds ?? [],
            }
        };
        userService.manageCompanyConfiguration(configurationParameters).then(response => {
            const responseData = response.getResponseData();
            const responseDataErrors = responseData.data?.errors;
            if (!responseData.success) {
                if (responseDataErrors) toast.error('Error al guardar los cambios. Propiedad: ' + responseData.data?.errors[0]?.value + ' - ' + responseData.data?.errors[0]?.message);
                else toast.error('Error al guardar los cambios: ' + responseData.message);
                return;
            } else if (responseData.success) {
                toast.success('Configuración editada con éxito.')
                invalidateBlockedEntryProductStatusesCache();
                setOriginalCompanyConfig(companyConfig);
                setHasCompanyConfigChanges(false);
            }
        }).catch(error => {
            toast.error('Error al guardar los cambios: ' + error);
        });
    }

    //////////////////////////////////////////////////////////////////////
    // FUNCTION TO DISCARD COMPANY CONFIG CHANGES
    //////////////////////////////////////////////////////////////////////
    const discardCompanyConfigChanges = () => {
        setCompanyConfig(originalCompanyConfig);
        setHasCompanyConfigChanges(false);
        formik.setValues({
            daysPeriodToEndContract: originalCompanyConfig?.daysPeriodToEndContract ?? '',
            contractCodeFormat: originalCompanyConfig?.contractCodeFormat ?? '',
            tutorAgreementDocumentTemplate: originalCompanyConfig?.tutorAgreementDocumentTemplate ?? '',
            incidenceCodeFormat: originalCompanyConfig?.incidenceCodeFormat ?? null,
            nonStandardInvoiceCodeFormat: originalCompanyConfig?.nonStandardInvoiceCodeFormat ?? '',
            festiveDays: originalCompanyConfig?.festiveDays ?? [],
            legalText: originalCompanyConfig?.legalText ?? '',
            smsText: originalCompanyConfig?.smsText ?? '',
            daysNeededToGenerateContract: originalCompanyConfig?.daysNeededToGenerateContract ?? '',
            tutorMaxMorningTutorials: originalCompanyConfig?.tutorMaxMorningTutorials ?? 1,
            tutorMaxAfternoonTutorials: originalCompanyConfig?.tutorMaxMorningTutorials ?? 1,
            daysBeforeExpirationCollectionDate: originalCompanyConfig?.daysBeforeExpirationCollectionDate ?? 2,
            daysAfterNonPaymentExpiration: originalCompanyConfig?.daysAfterNonPaymentExpiration ?? 2,
            activeContractCommissionModel: originalCompanyConfig?.activeContractCommissionModel ?? '',
            blockedContractProductStatuses: originalCompanyConfig?.blockedContractProductStatuses ?? [],
            facilitatorRoleIds: originalCompanyConfig?.facilitatorRoleIds ?? [],
            facilitatorManagerRoleIds: originalCompanyConfig?.facilitatorManagerRoleIds ?? [],
            facilitatorOrderStatusIds: originalCompanyConfig?.facilitatorOrderStatusIds ?? [],
        })
        toast.info('Cambios descartados');
    }

    return (
        <SettingsContext.Provider value={{
            companyConfig,
            setCompanyConfig,
            updateCompanyConfig,
            isLoading,
            setIsLoading,
            hasCompanyConfigChanges,
            applyCompanyConfigChanges,
            discardCompanyConfigChanges,
            showModal,
            setShowModal,
            showCacheModal,
            setShowCacheModal,
            showImportCRMModal,
            setShowImportCRMModal,
            originalCompanyConfig,
            formik,
        }}>
            {children}
        </SettingsContext.Provider>
    );
}

export { SettingsContext, SettingsProvider };

export function useSettingsProvider() {
    return useContext(SettingsContext);
}
